export const OFFICE_DROPZONE_ACCEPT: Record<string, string[]> = {
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

export const DOCUMENT_DROPZONE_ACCEPT: Record<string, string[]> = {
  "image/*": [],
  "application/pdf": [],
  ...OFFICE_DROPZONE_ACCEPT,
};

// CAD drawings, offered only on the final plans upload; browsers rarely send a mime, so the extension admits them.
export const CAD_DROPZONE_ACCEPT: Record<string, string[]> = {
  "application/acad": [".dwg"],
  "application/dxf": [".dxf"],
  "model/vnd.dwf": [".dwf"],
};

export const FINAL_PLANS_DROPZONE_ACCEPT: Record<string, string[]> = {
  ...DOCUMENT_DROPZONE_ACCEPT,
  ...CAD_DROPZONE_ACCEPT,
};

export const DOCUMENT_ACCEPT_STRING =
  "image/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const OFFICE_EXTENSIONS = [".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx"];

// Some browsers report CAD as an image mime, so this must win over the image check below or the file renders as a broken thumbnail.
export const CAD_EXTENSIONS = [".dwg", ".dxf", ".dwf"];

export const isCadFileName = (name: string): boolean =>
  CAD_EXTENSIONS.some((ext) => (name || "").toLowerCase().endsWith(ext));

// image/* is on the shared list and Linux registers .dwg as image/vnd.dwg, so
// the accept map alone cannot keep CAD off the other uploads.
export const acceptsCad = (accept: Record<string, string[]>): boolean =>
  Object.values(accept)
    .flat()
    .some((ext) => CAD_EXTENSIONS.includes(ext.toLowerCase()));

// The final plans picker is the ordinary one plus CAD; keep the two in step.
export const FINAL_PLANS_ACCEPT_STRING = `${DOCUMENT_ACCEPT_STRING},.dwg,.dxf,.dwf,application/acad,application/dxf,model/vnd.dwf`;

// Final plans keep their CAD attachments editable; every other revision does not.
export const uploadAcceptString = (allowCad: boolean): string =>
  allowCad ? FINAL_PLANS_ACCEPT_STRING : DOCUMENT_ACCEPT_STRING;

export const CAD_NOT_ALLOWED_CODE = "cad-not-allowed";

export const CAD_NOT_ALLOWED_MESSAGE =
  "CAD files can only be attached to final plans.";

// react-dropzone validator: accept cannot express "images but not CAD", so CAD is filtered explicitly.
export const cadValidator =
  (accept: Record<string, string[]>) =>
  (file: File): { code: string; message: string } | null => {
    if (acceptsCad(accept)) return null;
    return isCadFileName(file?.name ?? "")
      ? { code: CAD_NOT_ALLOWED_CODE, message: CAD_NOT_ALLOWED_MESSAGE }
      : null;
  };

// Plain file inputs have no dropzone validator, so they drop CAD themselves.
export const withoutCadFiles = (
  files: File[],
): { allowed: File[]; rejected: string[] } => {
  const allowed: File[] = [];
  const rejected: string[] = [];

  (files || []).forEach((file) => {
    if (isCadFileName(file?.name ?? "")) {
      rejected.push(file.name);
    } else {
      allowed.push(file);
    }
  });

  return { allowed, rejected };
};

export const filesAllowedFor = (
  files: File[],
  allowCad: boolean,
): { allowed: File[]; rejected: string[] } =>
  allowCad
    ? { allowed: [...(files || [])], rejected: [] }
    : withoutCadFiles(files);

export type UploadKind = "image" | "pdf" | "document";

export function getUploadKind(file: {
  type?: string;
  mime?: string;
  name?: string;
}): UploadKind {
  const mime = (file.mime || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (
    mime === "application/pdf" ||
    name.endsWith(".pdf") ||
    file.type === "pdf"
  ) {
    return "pdf";
  }
  if (CAD_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "document";
  }
  if (
    OFFICE_EXTENSIONS.some((ext) => name.endsWith(ext)) ||
    mime === "application/msword" ||
    mime.includes("officedocument") ||
    mime.includes("ms-powerpoint") ||
    mime.includes("ms-excel")
  ) {
    return "document";
  }
  if (mime.startsWith("image/") || file.type === "image") {
    return "image";
  }
  return "document";
}
